// src/products/capRaise/components/ValuationExitAnalysis.js
// Advanced scenario analysis with valuation flexibility and exit scenarios

import React, { useState, useEffect } from 'react';
import { TrendingUp, Target, DollarSign, AlertCircle } from 'lucide-react';
import { useMagnitude } from '../../../components/ui/MagnitudeSelector';

const flexibilityOptions = [
  { label: '±5%', value: '5%', multiplier: 0.05 },
  { label: '±10%', value: '10%', multiplier: 0.10 },
  { label: '±20%', value: '20%', multiplier: 0.20 },
  { label: '±30%', value: '30%', multiplier: 0.30 }
];

const ValuationExitAnalysis = ({ roundData, selectedCompany, user, addMessage }) => {
  const { formatCurrencyWithMagnitude } = useMagnitude();
  const [selectedFlexibility, setSelectedFlexibility] = useState('10%');
  const [exitScenarios, setExitScenarios] = useState({
    low: { multiplier: 2 },
    good: { multiplier: 5 },
    excellent: { multiplier: 10 }
  });
  const [exitYears, setExitYears] = useState(5);

  const [analysisResults, setAnalysisResults] = useState({
    valuationScenarios: [],
    exitAnalysis: {}
  });

  // Calculate valuation scenarios
  useEffect(() => {
    if (!roundData?.pre_money_valuation || !roundData?.target_amount) return;

    const baseValuation = parseFloat(roundData.pre_money_valuation);
    const targetAmount = parseFloat(roundData.target_amount);
    const selectedOption = flexibilityOptions.find(opt => opt.value === selectedFlexibility);
    
    if (!selectedOption) return;

    const scenarios = [
      {
        name: `-${selectedFlexibility}`,
        preMoneyValuation: baseValuation * (1 - selectedOption.multiplier),
        type: 'downside'
      },
      {
        name: 'Base Case',
        preMoneyValuation: baseValuation,
        type: 'base'
      },
      {
        name: `+${selectedFlexibility}`,
        preMoneyValuation: baseValuation * (1 + selectedOption.multiplier),
        type: 'upside'
      }
    ];

    const valuationScenarios = scenarios.map(scenario => {
      const postMoneyValuation = scenario.preMoneyValuation + targetAmount;
      const dilution = (targetAmount / postMoneyValuation) * 100;
      const investorOwnership = dilution;
      
      return {
        ...scenario,
        targetAmount,
        postMoneyValuation,
        dilution,
        investorOwnership
      };
    });

    setAnalysisResults(prev => ({
      ...prev,
      valuationScenarios
    }));
  }, [roundData, selectedFlexibility]);

  // Calculate exit analysis
  useEffect(() => {
    if (!analysisResults.valuationScenarios.length) return;

    const baseScenario = analysisResults.valuationScenarios.find(s => s.type === 'base');
    if (!baseScenario) return;

    const targetAmount = parseFloat(roundData?.target_amount) || 0;
    const exitAnalysis = {};
    
    Object.entries(exitScenarios).forEach(([scenarioName, config]) => {
      const exitValuation = baseScenario.postMoneyValuation * config.multiplier;
      const investorProceeds = (baseScenario.investorOwnership / 100) * exitValuation;
      const investorIRR = calculateIRR(targetAmount, investorProceeds, exitYears);
      const investorMultiple = investorProceeds / targetAmount;

      exitAnalysis[scenarioName] = {
        exitValuation,
        investorProceeds,
        investorIRR,
        investorMultiple,
        years: exitYears,
        multiplier: config.multiplier
      };
    });

    setAnalysisResults(prev => ({
      ...prev,
      exitAnalysis
    }));
  }, [analysisResults.valuationScenarios, exitScenarios, roundData?.target_amount, exitYears]);

  const calculateIRR = (initialInvestment, finalValue, years) => {
    if (initialInvestment <= 0 || finalValue <= 0) return 0;
    return Math.pow(finalValue / initialInvestment, 1 / years) - 1;
  };

  const formatPercentage = (value) => {
    return `${(value * 100).toFixed(1)}%`;
  };

  const getScenarioColor = (type) => {
    switch (type) {
      case 'downside': return 'text-red-600 bg-red-50 border-red-200';
      case 'base': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'upside': return 'text-green-600 bg-green-50 border-green-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getExitScenarioColor = (scenarioName) => {
    switch (scenarioName) {
      case 'low': return 'text-red-600 bg-red-50 border-red-200';
      case 'good': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'excellent': return 'text-green-600 bg-green-50 border-green-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  // Check for missing information
  const missingInfo = [];
  if (!roundData?.pre_money_valuation) missingInfo.push('Pre-money valuation');
  if (!roundData?.target_amount) missingInfo.push('Target raise amount');
  if (!roundData?.currency) missingInfo.push('Currency');
  if (!selectedCompany?.name) missingInfo.push('Company name');

  if (missingInfo.length > 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-3">
          <AlertCircle className="w-6 h-6 text-red-600" />
          <h2 className="text-xl font-semibold text-gray-900">Missing Information</h2>
        </div>
        
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-red-900 mb-4">Required Information Missing</h3>
          <p className="text-red-800 mb-4">
            The following information is needed to perform scenario analysis:
          </p>
          <ul className="list-disc list-inside space-y-2 text-red-700">
            {missingInfo.map((info, index) => (
              <li key={index}>{info}</li>
            ))}
          </ul>
          <p className="text-red-800 mt-4 text-sm">
            Please complete the round details in the "New Raise" page before running scenario analysis.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Valuation Flexibility Selector */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Pre-Money Valuation Flexibility</h3>
        
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Select valuation flexibility range:
          </label>
          <div className="flex space-x-3">
            {flexibilityOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => setSelectedFlexibility(option.value)}
                className={`px-4 py-2 rounded-md border transition-colors ${
                  selectedFlexibility === option.value
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Valuation Scenarios */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {analysisResults.valuationScenarios.map((scenario, index) => (
            <div key={index} className={`border rounded-lg p-4 ${getScenarioColor(scenario.type)}`}>
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold">{scenario.name}</h4>
                <TrendingUp className="w-5 h-5" />
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Pre-Money:</span>
                  <span className="font-medium">{formatCurrencyWithMagnitude(scenario.preMoneyValuation)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Post-Money:</span>
                  <span className="font-medium">{formatCurrencyWithMagnitude(scenario.postMoneyValuation)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Investor Ownership:</span>
                  <span className="font-medium">{scenario.investorOwnership.toFixed(1)}%</span>
                </div>
                <div className="flex justify-between">
                  <span>Founder Dilution:</span>
                  <span className="font-medium">{scenario.dilution.toFixed(1)}%</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Exit Scenario Analysis */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <Target className="w-6 h-6 text-green-600" />
          <h3 className="text-lg font-semibold text-gray-900">Exit Scenario Analysis</h3>
        </div>

        <div className="mb-6">
          <p className="text-sm text-gray-600 mb-4">
            Based on the base case scenario, here are potential exit outcomes for investors:
          </p>
          
          {/* Exit Scenario Controls */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Years to Exit (all scenarios)</label>
            <input
              type="number"
              min="0"
              value={exitYears}
              onChange={(e) => setExitYears(parseFloat(e.target.value) || 0)}
              className="w-full md:w-64 px-3 py-2 border border-gray-300 rounded text-sm"
              placeholder="Years"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {Object.entries(exitScenarios).map(([scenarioName, config]) => (
              <div key={scenarioName} className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 capitalize">
                  {scenarioName} Exit
                </label>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Exit Multiple (x)</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    value={config.multiplier}
                    onChange={(e) => setExitScenarios(prev => ({
                      ...prev,
                      [scenarioName]: { ...config, multiplier: parseFloat(e.target.value) || 0 }
                    }))}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    placeholder="Multiple"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Exit Results */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {Object.entries(analysisResults.exitAnalysis).map(([scenarioName, data]) => (
            <div key={scenarioName} className={`border rounded-lg p-4 ${getExitScenarioColor(scenarioName)}`}>
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold capitalize">{scenarioName} Exit</h4>
                <DollarSign className="w-5 h-5" />
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Exit Valuation:</span>
                  <span className="font-medium">{formatCurrencyWithMagnitude(data.exitValuation)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Investor Proceeds:</span>
                  <span className="font-medium">{formatCurrencyWithMagnitude(data.investorProceeds)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Multiple:</span>
                  <span className="font-medium">{data.investorMultiple.toFixed(1)}x</span>
                </div>
                <div className="flex justify-between">
                  <span>IRR:</span>
                  <span className="font-medium">{formatPercentage(data.investorIRR)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Timeframe:</span>
                  <span className="font-medium">{data.years} years</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Summary Insights */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-4">💡 Analysis Insights</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
          <div>
            <h4 className="font-medium text-blue-900 mb-2">Valuation Flexibility</h4>
            <ul className="space-y-1 text-blue-800">
              <li>• Higher valuations reduce founder dilution</li>
              <li>• Lower valuations may be easier to achieve</li>
              <li>• Consider market conditions and competition</li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-medium text-blue-900 mb-2">Exit Scenarios</h4>
            <ul className="space-y-1 text-blue-800">
              <li>• IRR above 20% is considered good</li>
              <li>• 3-5x multiple is typical for successful exits</li>
              <li>• Timeframe affects IRR significantly</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ValuationExitAnalysis;
